import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Mail, Camera } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rejuven-health/artifacts/rqq06olv_Rejuven%20Logo%20-%20Website.png';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#FAF8F7] text-[#2B2625]">
      {/* Header */}
      <header className="border-b border-[#E8D5D1]/60 bg-white/70 backdrop-blur-2xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center"><img src={LOGO_URL} alt="Rejuven" className="h-11 w-auto" /></Link>
            <Link to="/" className="flex items-center gap-2 text-[#6C6361] hover:text-[#2B2625] text-sm"><ArrowLeft className="w-4 h-4" />Back to Home</Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#F3EFEF] flex items-center justify-center"><Shield className="w-6 h-6 text-[#C28B87]" /></div>
          <div>
            <h1 className="font-display text-4xl text-[#2B2625] font-medium">Privacy Policy</h1>
            <p className="text-[#6C6361] text-sm">Last updated: December 2025</p>
          </div>
        </div>

        <div className="prose prose-lg max-w-none text-[#6C6361] prose-headings:font-display prose-headings:text-[#2B2625] prose-headings:font-medium prose-strong:text-[#2B2625] prose-a:text-[#C28B87] prose-li:marker:text-[#C28B87]">
          <p className="lead text-lg">
            At Rejuven Inc. (&ldquo;Rejuven&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;), your privacy matters deeply. This Privacy Policy explains how we collect, use, disclose and safeguard your information when you use our microbiome and genotype-based precision wellness platform. Rejuven provides educational, preventative wellness guidance only and is not a medical provider.
          </p>

          <div className="bg-white rounded-2xl p-6 border border-[#E8D5D1] my-8 not-prose">
            <div className="flex items-start gap-4">
              <Lock className="w-6 h-6 text-[#C28B87] flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-[#2B2625] mb-2">Your data is protected</h3>
                <p className="text-sm text-[#6C6361]">Personal and health-related information is encrypted in transit and at rest. We never sell your data, and biometric information is used only for the wellness purposes you consent to.</p>
              </div>
            </div>
          </div>

          <h2 className="flex items-center gap-2"><Database className="w-5 h-5 text-[#899285]" />Information We Collect</h2>
          <p>We collect information you provide directly and information generated as you use the platform, including:</p>
          <ul>
            <li><strong>Account Information:</strong> Name, email address and password.</li>
            <li><strong>Wellness Profile:</strong> Age, sex, life stage, health and family history, goals, dietary preferences and lifestyle details you choose to share.</li>
            <li><strong>Biometric Data:</strong> If you opt in to camera-based analysis, facial images and derived visual markers used to help build an educational estimate of your likely genotype tendencies.</li>
            <li><strong>Genotype & Microbiome Signals:</strong> Educational estimates and inferences derived from your questionnaires, biometric analysis and lifestyle inputs. Rejuven does not perform clinical genetic sequencing.</li>
            <li><strong>Wellness Activity:</strong> Check-in responses, tracked metrics and preferences.</li>
            <li><strong>Communication Data:</strong> Messages sent to &ldquo;Dr. Erika&rdquo;, our AI wellness companion.</li>
            <li><strong>Uploaded Content:</strong> Documents or notes you choose to share.</li>
            <li><strong>Usage Data:</strong> Device, log and analytics information to operate and improve the platform.</li>
          </ul>

          <h2 className="flex items-center gap-2"><Camera className="w-5 h-5 text-[#899285]" />Biometric &amp; Genotype Information</h2>
          <p>Because our approach can involve optional camera-based (biometric) analysis, we treat this information with particular care:</p>
          <ul>
            <li><strong>Consent-first:</strong> Biometric analysis is entirely optional and only occurs after you provide explicit, informed consent. You can decline and still use other features.</li>
            <li><strong>Limited purpose:</strong> Biometric and genotype signals are used solely to generate educational, personalized wellness guidance — never for identity verification, surveillance, advertising or resale.</li>
            <li><strong>Educational, not diagnostic:</strong> Any genotype or microbiome estimate is an educational inference, not a clinical diagnosis or genetic test result.</li>
            <li><strong>Security &amp; retention:</strong> Biometric data is encrypted and retained only as long as needed for your guidance or as required by law. You may request deletion at any time.</li>
            <li><strong>No sale:</strong> We do not sell, license or share biometric or genotype information with third parties for their own use.</li>
          </ul>

          <h2 className="flex items-center gap-2"><Eye className="w-5 h-5 text-[#899285]" />How We Use Your Information</h2>
          <ul>
            <li>Provide personalized, preventative wellness guidance based on your unique profile and biology.</li>
            <li>Power our AI wellness companion to offer relevant, educational information.</li>
            <li>Generate educational genotype and microbiome insights across your goals and life stage.</li>
            <li>Send you helpful notifications and product updates you opt into.</li>
            <li>Improve, secure and develop our services.</li>
          </ul>

          <h2 className="flex items-center gap-2"><UserCheck className="w-5 h-5 text-[#899285]" />Data Sharing &amp; Disclosure</h2>
          <p>We do not sell, trade or rent your personal information. We share information only:</p>
          <ul>
            <li><strong>With your consent</strong>, when you explicitly authorize it.</li>
            <li><strong>With trusted service providers</strong> who help us operate the platform under confidentiality obligations.</li>
            <li><strong>When legally required</strong>, to comply with law or protect rights and safety.</li>
          </ul>

          <h2>Your Rights &amp; Choices</h2>
          <ul>
            <li>Access, update or delete your personal information at any time.</li>
            <li>Withdraw consent for biometric analysis and request deletion of biometric data.</li>
            <li>Export your data in a portable format.</li>
            <li>Opt out of promotional communications.</li>
            <li>Request deletion of your account and associated data.</li>
          </ul>

          <h2>Data Security</h2>
          <ul>
            <li>Encryption of data in transit and at rest.</li>
            <li>Access controls and authentication protocols.</li>
            <li>Regular security reviews and safeguards.</li>
          </ul>

          <h2>Children&apos;s Privacy</h2>
          <p>Rejuven accounts are intended for adults (18+). Child nutrition guidance is provided to parents and caregivers about their children; we do not knowingly create accounts for or collect data directly from children.</p>

          <h2>Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated &ldquo;Last updated&rdquo; date.</p>

          <div className="bg-[#F3EFEF] rounded-2xl p-6 my-8 not-prose">
            <h3 className="flex items-center gap-2 text-[#2B2625] font-semibold mb-2"><Mail className="w-5 h-5 text-[#C28B87]" />Contact Us</h3>
            <p className="text-sm text-[#6C6361]">Questions about this Privacy Policy? Email us at <a href="mailto:privacy@rejuvenhealth.com" className="text-[#C28B87] font-medium">privacy@rejuvenhealth.com</a>.</p>
          </div>

          <p className="text-xs text-[#6C6361]"><strong className="text-[#2B2625]">Medical disclaimer:</strong> Rejuven provides educational and preventative wellness information only. It is not medical advice and is not a substitute for professional diagnosis, treatment or the advice of your licensed physician.</p>
        </div>
      </main>

      <footer className="border-t border-[#E8D5D1]/60 py-8 mt-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-[#6C6361]">© 2026 Rejuven Inc. All rights reserved.</div>
      </footer>
    </div>
  );
}
